import React, { useState, useEffect, useRef } from "react";
import http from "../../services/http";
import { fetchCitiesByStates } from "../../services/tenders";
import { Select } from "../Core";

// Validation helpers
const validateEmail = (value) => {
  if (!value || !value.trim()) return "Email is required.";
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!re.test(value.trim())) return "Please enter a valid email address.";
  return "";
};

const validateMobile = (value) => {
  if (!value || !value.trim()) return "Mobile number is required.";
  const digits = value.replace(/\D/g, "");
  if (digits.length < 10) return "Please enter a valid 10-digit mobile number.";
  if (digits.length > 15) return "Mobile number is too long.";
  return "";
};

const validateRequired = (value, fieldName, minLength = 1) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return `${fieldName} is required.`;
  if (minLength > 1 && trimmed.length < minLength) return `${fieldName} must be at least ${minLength} characters.`;
  return "";
};

const InquiryForm = ({ page = "contact" }) => {
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    mobile: "",
    email: "",
    city: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [cityOptions, setCityOptions] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const cityValueRef = useRef("");

  useEffect(() => {
    cityValueRef.current = formData.city;
  }, [formData.city]);

  useEffect(() => {
    let mounted = true;
    setCitiesLoading(true);
    fetchCitiesByStates([])
      .then((list) => {
        if (!mounted || !Array.isArray(list)) return;
        const seen = new Set();
        const opts = [];
        list.forEach((c) => {
          const raw =
            typeof c === "string"
              ? { value: c.trim(), label: c.trim() }
              : {
                  value: String(c.value || c.name || c.city || c.label || "").trim(),
                  label: String(c.label || c.name || c.city || c.value || "").trim(),
                };
          if (!raw.value) return;
          if (seen.has(raw.value)) return;
          seen.add(raw.value);
          opts.push({ value: raw.value, label: raw.label || raw.value });
        });
        opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
        setCityOptions(opts);
      })
      .catch(() => {
        if (mounted) setCityOptions([]);
      })
      .finally(() => {
        if (mounted) setCitiesLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSubmitStatus(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const nameErr = validateRequired(formData.name, "Name", 2);
    if (nameErr) newErrors.name = nameErr;
    const emailErr = validateEmail(formData.email);
    if (emailErr) newErrors.email = emailErr;
    const mobileErr = validateMobile(formData.mobile);
    if (mobileErr) newErrors.mobile = mobileErr;
    const cityErr = validateRequired(formData.city, "City", 2);
    if (cityErr) newErrors.city = cityErr;
    const messageErr = validateRequired(formData.message, "Message");
    if (messageErr) newErrors.message = messageErr;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    let err = "";
    if (name === "name") err = validateRequired(formData.name, "Name", 2);
    else if (name === "email") err = validateEmail(formData.email);
    else if (name === "mobile") err = validateMobile(formData.mobile);
    else if (name === "city") err = validateRequired(formData.city, "City", 2);
    else if (name === "message") err = validateRequired(formData.message, "Message");
    if (err) setErrors((prev) => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    if (!validateForm()) return;
    const mobileno = formData.mobile.replace(/\D/g, "");
    const payload = {
      page: typeof page === "string" && page.trim() ? page.trim() : "contact",
      name: formData.name.trim(),
      companyname: formData.companyName.trim(),
      mobileno,
      email: formData.email.trim(),
      city: formData.city.trim(),
      message: formData.message.trim(),
    };
    setSubmitting(true);
    try {
      await http.post("/common-contact-form/", payload);
      setSubmitStatus({
        type: "success",
        message: "Thank you. Your message has been sent. We will get back to you soon.",
      });
      setFormData({
        name: "",
        companyName: "",
        mobile: "",
        email: "",
        city: "",
        message: "",
      });
      setErrors({});
    } catch (err) {
      const data = err.response?.data;
      let msg = "Something went wrong. Please try again.";
      if (typeof data === "string" && data.trim()) msg = data.trim();
      else if (data && typeof data === "object") {
        if (data.message) msg = String(data.message);
        else if (data.detail) msg = String(data.detail);
        else if (Array.isArray(data.non_field_errors) && data.non_field_errors[0]) {
          msg = String(data.non_field_errors[0]);
        }
      }
      setSubmitStatus({ type: "error", message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inquiry-form-wrapper">
      <style>
        {`
          .inquiry-form-wrapper {
            width: 100%;
          }
          .inquiry-form-container {
            width: 100%;
            background: #ffffff;
            border-radius: 12px;
            padding: 40px;
            box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
          }
          .inquiry-form-group {
            margin-bottom: 24px;
          }
          .inquiry-form-row {
            display: flex;
            gap: 20px;
            margin-bottom: 24px;
          }
          .inquiry-form-row .inquiry-form-group {
            flex: 1;
            margin-bottom: 0;
          }
          .inquiry-form-label {
            display: block;
            font-size: 0.95rem;
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 8px;
          }
          .inquiry-form-input,
          .inquiry-form-textarea {
            width: 100%;
            padding: 12px 16px;
            font-size: 1rem;
            color: #1e293b;
            background: #ffffff;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            transition: all 0.3s ease;
            font-family: inherit;
          }
          .inquiry-form-input:focus,
          .inquiry-form-textarea:focus {
            outline: none;
            border-color: #22c55e;
            box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
          }
          .inquiry-form-input.is-invalid,
          .inquiry-form-textarea.is-invalid {
            border-color: #dc3545;
          }
          .inquiry-form-input.is-invalid:focus,
          .inquiry-form-textarea.is-invalid:focus {
            border-color: #dc3545;
            box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
          }
          .inquiry-form-error {
            font-size: 0.875rem;
            color: #dc3545;
            margin-top: 6px;
          }
          .inquiry-form-textarea {
            min-height: 120px;
            resize: vertical;
          }
          .inquiry-form-button {
            width: 100%;
            padding: 14px 24px;
            font-size: 1rem;
            font-weight: 600;
            color: #ffffff;
            background: #06b074;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .inquiry-form-button:hover {
            background: #059660;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(6, 176, 116, 0.3);
          }
          .inquiry-form-button:active {
            transform: translateY(0);
          }
          @media (max-width: 768px) {
            .inquiry-form-container {
              padding: 32px 24px;
            }
            .inquiry-form-row {
              flex-direction: column;
              gap: 0;
            }
          }
        `}
      </style>
      <div className="inquiry-form-container">
        <form onSubmit={handleSubmit}>
        {submitStatus && (
          <div
            className={`alert mb-6 ${submitStatus.type === "success" ? "alert-success" : "alert-danger"}`}
            role="alert"
          >
            {submitStatus.message}
          </div>
        )}
        <div className="inquiry-form-group">
          <label htmlFor="name" className="inquiry-form-label">
            Your Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`inquiry-form-input ${errors.name ? "is-invalid" : ""}`}
            placeholder="Your Name"
            required
            minLength={2}
          />
          {errors.name && <p className="inquiry-form-error mb-0" role="alert">{errors.name}</p>}
        </div>

        <div className="inquiry-form-row">
          <div className="inquiry-form-group">
            <label htmlFor="companyName" className="inquiry-form-label">
              Company Name
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="inquiry-form-input"
              placeholder="Company Name"
            />
          </div>
          <div className="inquiry-form-group">
            <label htmlFor="mobile" className="inquiry-form-label">
              Mobile No
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`inquiry-form-input ${errors.mobile ? "is-invalid" : ""}`}
              placeholder="10-digit mobile number"
              inputMode="numeric"
              maxLength={15}
            />
            {errors.mobile && <p className="inquiry-form-error mb-0" role="alert">{errors.mobile}</p>}
          </div>
        </div>

        <div className="inquiry-form-row">
          <div className="inquiry-form-group">
            <label htmlFor="email" className="inquiry-form-label">
              E-mail
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`inquiry-form-input ${errors.email ? "is-invalid" : ""}`}
              placeholder="example@gmail.com"
              required
            />
            {errors.email && <p className="inquiry-form-error mb-0" role="alert">{errors.email}</p>}
          </div>

          <div className="inquiry-form-group">
            <label htmlFor="inquiry-city" className="inquiry-form-label">
              City
            </label>
            {citiesLoading ? (
              <div className="w-100">
                <Select
                  inputId="inquiry-city"
                  instanceId="inquiry-city-loading"
                  name="city"
                  options={[]}
                  placeholder="Loading cities…"
                  isLoading
                  isDisabled
                  menuIsOpen={false}
                  border
                  accentColor="success"
                />
              </div>
            ) : cityOptions.length > 0 ? (
              <div className="w-100">
                <Select
                  inputId="inquiry-city"
                  instanceId="inquiry-city"
                  name="city"
                  options={cityOptions}
                  placeholder="Type to search or select city"
                  isSearchable
                  isClearable
                  openMenuOnFocus
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  maxMenuHeight={220}
                  border
                  accentColor="success"
                  value={
                    formData.city
                      ? cityOptions.find(
                          (o) => o.value === formData.city || o.label === formData.city,
                        ) || null
                      : null
                  }
                  onChange={(selectedOption) => {
                    const next = selectedOption
                      ? String(selectedOption.value || selectedOption.label || "").trim()
                      : "";
                    cityValueRef.current = next;
                    setFormData((prev) => ({ ...prev, city: next }));
                    if (errors.city) setErrors((prev) => ({ ...prev, city: "" }));
                    setSubmitStatus(null);
                  }}
                  onBlur={() => {
                    const err = validateRequired(cityValueRef.current, "City", 2);
                    if (err) setErrors((prev) => ({ ...prev, city: err }));
                  }}
                />
              </div>
            ) : (
              <input
                type="text"
                id="inquiry-city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`inquiry-form-input ${errors.city ? "is-invalid" : ""}`}
                placeholder="City"
                required
                minLength={2}
              />
            )}
            {errors.city && <p className="inquiry-form-error mb-0" role="alert">{errors.city}</p>}
          </div>
        </div>

        <div className="inquiry-form-group">
          <label htmlFor="message" className="inquiry-form-label">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`inquiry-form-textarea ${errors.message ? "is-invalid" : ""}`}
            placeholder="Type your message"
            required
          />
          {errors.message && <p className="inquiry-form-error mb-0" role="alert">{errors.message}</p>}
        </div>

        <button
          type="submit"
          className="inquiry-form-button"
          disabled={submitting || citiesLoading}
        >
          {submitting ? "Sending…" : "SEND NOW"}
        </button>
      </form>
      </div>
    </div>
  );
};

export default InquiryForm;

